import React, { useCallback, useMemo } from 'react';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { Box, Typography } from '@mui/material';
import {
  SiteChoice,
  SiteOption,
  SmartImportAnalysis,
  SmartImportSourceRow,
} from '../types';
import { NONE_SITE_CHOICE, SiteSelect } from './SiteSelect';
import { useIntl } from 'react-intl';
import { useMaterialReactTableLocalization } from '../../../i18n';

const tablePaperStyles = { display: 'grid', flexGrow: 1, minHeight: 0 };
const tableContainerStyles = { maxHeight: '100%' };
const siteCellStyles = { minWidth: 220, py: 0.5 };
const rightAlignedCellProps = { align: 'right' as const };
const simpleNumberPattern = /^[+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+)$/;

function isNumericPreviewColumn(
  header: string,
  rows: SmartImportSourceRow[]
): boolean {
  const values = rows
    .map((row) =>
      row.cells.find((cell) => cell.column === header)?.value?.trim()
    )
    .filter((value): value is string => Boolean(value));

  return (
    values.length > 0 &&
    values.every((value) => simpleNumberPattern.test(value))
  );
}

interface RowSiteSelectProps {
  globalSite: SiteChoice;
  row: SmartImportSourceRow;
  rowSiteId: string | null | undefined;
  sites: SiteOption[];
  onChange: (rowRef: string, value: SiteChoice | null) => void;
}

const RowSiteSelect = React.memo(
  ({ globalSite, row, rowSiteId, sites, onChange }: RowSiteSelectProps) => {
    const intl = useIntl();
    const value = useMemo(() => {
      if (rowSiteId === undefined) {
        return null;
      }

      if (rowSiteId === null) {
        return NONE_SITE_CHOICE;
      }

      return sites.find((site) => site.id === rowSiteId) || null;
    }, [rowSiteId, sites]);
    const handleChange = useCallback(
      (choice: SiteChoice | null) => onChange(row.rowRef, choice),
      [onChange, row.rowRef]
    );

    return (
      <Box sx={siteCellStyles}>
        <SiteSelect
          allowUnselected
          label={intl.formatMessage({
            id: 'imports.preview.rowSiteOverride',
            defaultMessage: 'Row site override',
          })}
          onChange={handleChange}
          placeholder={intl.formatMessage(
            {
              id: 'imports.preview.inheritSite',
              defaultMessage: 'Inherit {site}',
            },
            {
              site:
                globalSite.id === null
                  ? intl.formatMessage({
                      id: 'general.none',
                      defaultMessage: 'None',
                    })
                  : globalSite.name,
            }
          )}
          sites={sites}
          value={value}
        />
      </Box>
    );
  }
);

interface ImportPreviewTableProps {
  analysis: SmartImportAnalysis;
  globalSite: SiteChoice;
  rowOverrides: Record<string, string | null>;
  sites: SiteOption[];
  onRowSiteChange: (rowRef: string, value: SiteChoice | null) => void;
}

export const ImportPreviewTable = React.memo(
  ({
    analysis,
    globalSite,
    rowOverrides,
    sites,
    onRowSiteChange,
  }: ImportPreviewTableProps) => {
    const intl = useIntl();
    const localization = useMaterialReactTableLocalization();
    const columns = useMemo<MRT_ColumnDef<SmartImportSourceRow>[]>(
      () => [
        {
          accessorKey: 'sourceRowNumber',
          header: intl.formatMessage({
            id: 'imports.sourceRow',
            defaultMessage: 'Source row',
          }),
          muiTableBodyCellProps: rightAlignedCellProps,
          muiTableHeadCellProps: rightAlignedCellProps,
          size: 90,
        },
        ...analysis.headers.map((header) => {
          const numeric = isNumericPreviewColumn(header, analysis.previewRows);
          const alignmentProps = numeric ? rightAlignedCellProps : undefined;

          return {
            id: header,
            accessorFn: (row: SmartImportSourceRow) =>
              row.cells.find((cell) => cell.column === header)?.value || '',
            header,
            muiTableBodyCellProps: alignmentProps,
            muiTableHeadCellProps: alignmentProps,
            size: 160,
          };
        }),
        {
          id: 'site',
          header: intl.formatMessage({
            id: 'general.site',
            defaultMessage: 'Site',
          }),
          size: 240,
          Cell: ({ row }) => (
            <RowSiteSelect
              globalSite={globalSite}
              onChange={onRowSiteChange}
              row={row.original}
              rowSiteId={rowOverrides[row.original.rowRef]}
              sites={sites}
            />
          ),
        },
      ],
      [
        analysis.headers,
        analysis.previewRows,
        globalSite,
        intl,
        onRowSiteChange,
        rowOverrides,
        sites,
      ]
    );
    const renderToolbarActions = useCallback(
      () => (
        <Typography px={1} py={0.5} variant="body2">
          {intl.formatMessage(
            {
              id: 'imports.preview.rowCount',
              defaultMessage: 'Previewing {visible} of {total} rows',
            },
            {
              visible: analysis.previewRows.length,
              total: analysis.rowCount,
            }
          )}
        </Typography>
      ),
      [analysis.previewRows.length, analysis.rowCount, intl]
    );
    const table = useMaterialReactTable({
      columns,
      data: analysis.previewRows,
      localization,
      enableBottomToolbar: false,
      enableColumnActions: false,
      enableColumnFilters: false,
      enablePagination: false,
      enableToolbarInternalActions: false,
      initialState: { density: 'compact' },
      muiTableContainerProps: { sx: tableContainerStyles },
      muiTablePaperProps: { sx: tablePaperStyles },
      renderTopToolbarCustomActions: renderToolbarActions,
    });

    return <MaterialReactTable table={table} />;
  }
);
